import 'reflect-metadata';
import { ID } from 'appwrite';
import { container, singleton } from 'tsyringe';
import { appClient } from '../lib/appwrite/appClient';
import { APP_COLLECTIONS, AppUser, UserSettings } from '@polybank/shared';
import { queryClient } from '@/main';
import { logger, getErrorMessage } from '@/utils/logger';

@singleton()
class AppwriteAuthService {
  /** @internal */ currentUser: AppUser | undefined = undefined;
  /** @internal */ readonly account = appClient.account;
  /** @internal */ readonly tables = appClient.tables;
  /** @internal */ isCreatingGuestSession = false;
  /** @internal */ lastGuestSessionAttempt = 0;
  /** @internal */ readonly guestSessionCooldownMs = 5000;
  /** @internal */ readonly rateLimitCooldownMs = 30000;

  isInitialized = false;

  async initialize(): Promise<AppUser | undefined> {
    if (this.currentUser !== undefined) {
      logger.debug('Auth service already initialized');
      return this.currentUser;
    }

    logger.debug('Initializing authentication...');

    try {
      const result = await this.performInitialization();
      return result;
    } catch (error) {
      logger.error('Failed to initialize:', error);
      throw error;
    } finally {
      this.isInitialized = true;
    }
  }

  /** @internal */
  async performInitialization(): Promise<AppUser | undefined> {
    try {
      this.currentUser = await this.account.get();
    } catch (error) {
      const message = getErrorMessage(error);
      logger.debug('User not authenticated:', message);
      this.currentUser = undefined;
      return undefined;
    }

    // Detect anonymous (guest) sessions - they have no email and no email verification
    const isAnonymousSession =
      !this.currentUser?.email && !this.currentUser.emailVerification;

    logger.debug(
      'User authenticated:',
      this.currentUser.name,
      this.currentUser.email,
      'Anonymous:',
      isAnonymousSession
    );

    if (isAnonymousSession) {
      this.currentUser = {
        ...this.currentUser,
        name: 'Guest User',
        isGuest: true,
      };
      logger.debug('Detected existing guest session');
    }

    return this.currentUser;
  }

  async createAccount(
    email: string,
    password: string,
    name: string
  ): Promise<AppUser> {
    try {
      await this.account.create({
        userId: ID.unique(),
        email,
        password,
        name,
      });

      await this.account.createEmailPasswordSession({ email, password });
      await this.createUserDefaultSettings();

      // Load user with prefs after account creation
      const user = await this.account.get();
      const prefs = await this.account.getPrefs<UserSettings>();
      this.currentUser = { ...user, prefs };

      logger.debug('Account created successfully');
      return this.currentUser;
    } catch (error) {
      logger.error('Failed to create account:', error);
      throw error;
    }
  }

  async loginWithEmailPassword(
    email: string,
    password: string
  ): Promise<AppUser> {
    try {
      await this.account.createEmailPasswordSession({ email, password });
      const user = await this.account.get<UserSettings>();

      // Load prefs immediately after session creation
      const prefs = await this.account.getPrefs<UserSettings>();
      this.currentUser = { ...user, prefs };

      await this.updatePreferences({
        last_active_at: new Date().toISOString(),
      });

      logger.debug('Login successful');
      return this.currentUser;
    } catch (error) {
      logger.error('Failed to login:', error);
      throw error;
    }
  }

  async signInAndMergeGuestData(
    email: string,
    password: string
  ): Promise<AppUser> {
    if (!this.currentUser?.isGuest) {
      throw new Error('Can only merge guest data');
    }

    try {
      // Check if guest has any data worth migrating
      const hasData = await this.hasGuestData();

      if (!hasData) {
        logger.debug('No guest data found, using simple sign-in');
        return await this.simpleSignInFromGuest(email, password);
      }

      const guestUserId = this.currentUser.$id;
      logger.debug('Guest has data, proceeding with full migration');

      const response = await fetch('/api/upgrade-guest-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guestUserId,
          email,
          password,
          name: '',
          mode: 'signin',
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to sign in and merge data');
      }

      logger.debug('Sign-in and data merge completed');

      // Clear guest session before creating authenticated session
      try {
        await this.account.deleteSession('current');
        logger.debug('Guest session cleared');
      } catch (sessionError) {
        logger.warn(
          'Could not clear guest session (may already be invalid):',
          sessionError
        );
      }

      await this.account.createEmailPasswordSession({ email, password });
      const authenticatedUser = await this.account.get<UserSettings>();

      try {
        this.currentUser.prefs = await this.account.getPrefs<UserSettings>();
        logger.debug('User profile loaded');
      } catch (error) {
        logger.warn('Failed to load user profile after sign-in:', error);
      }

      this.currentUser = { ...authenticatedUser, isGuest: false };
      logger.debug(
        'Guest data merged with existing user:',
        this.currentUser.name
      );

      return this.currentUser;
    } catch (error) {
      logger.error('Failed to sign in and merge guest data:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.account.deleteSessions();
      queryClient.clear();
      localStorage.clear();
      this.currentUser = undefined;
    } catch (error) {
      logger.error('Failed to logout:', error);
      throw error;
    }
  }

  getCurrentUser(): AppUser | undefined {
    return this.currentUser;
  }

  getUserSettings(): UserSettings | undefined {
    return this.currentUser?.prefs;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== undefined;
  }

  async getJWT(): Promise<string> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const jwt = await this.account.createJWT();
      return jwt.jwt;
    } catch (error) {
      logger.error('Failed to create JWT:', error);
      throw new Error('Failed to create JWT token');
    }
  }

  /** @internal */
  async createUserDefaultSettings() {
    try {
      await this.account.updatePrefs<UserSettings>({
        prefs: {
          preferred_currency: 'EUR',
          date_format: 'DD/MM/YYYY',
          onboarding_completed: 'false',
          last_active_at: new Date().toISOString(),
        },
      });
    } catch (error) {
      logger.error('Failed to create user profile:', error);
      throw error;
    }
  }

  async updatePreferences(
    updates: Partial<UserSettings & { name: string }>
  ): Promise<UserSettings> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Fetch fresh preferences from server to avoid stale data overwrites
      const currentPrefs = await this.account.getPrefs<UserSettings>();

      const promises: Promise<AppUser>[] = [];
      if (updates.name) {
        promises.push(this.account.updateName({ name: updates.name }));
      }

      // Merge fresh prefs with updates
      promises.push(
        this.account.updatePrefs<UserSettings>({
          prefs: { ...currentPrefs, ...updates },
        })
      );

      await Promise.all(promises);

      const [updatedUser, updatedProfile] = await Promise.all([
        this.account.get<UserSettings>(),
        this.account.getPrefs<UserSettings>(),
      ]);

      this.currentUser = { ...updatedUser, prefs: updatedProfile };

      return updatedProfile;
    } catch (error) {
      logger.error('Failed to update profile:', error);
      throw error;
    }
  }

  async completeOnboarding(): Promise<void> {
    await this.updatePreferences({ onboarding_completed: 'true' });
  }

  /**
   * Creates an anonymous guest session with rate limiting and exponential backoff retry.
   * Prevents concurrent creation and API flooding via cooldown periods.
   */
  async createGuestSession(): Promise<AppUser> {
    // Prevent concurrent guest session creation
    if (this.isCreatingGuestSession) {
      logger.debug('Guest session creation already in progress, waiting...');
      while (this.isCreatingGuestSession) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (this.currentUser?.isGuest) {
        return this.currentUser;
      }
    }

    // Check cooldown period to prevent API flooding
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastGuestSessionAttempt;
    if (timeSinceLastAttempt < this.guestSessionCooldownMs) {
      const remainingCooldown =
        this.guestSessionCooldownMs - timeSinceLastAttempt;
      logger.debug(
        `Guest session creation in cooldown, waiting ${Math.ceil(
          remainingCooldown / 1000
        )}s...`
      );
      throw new Error(
        `Please wait ${Math.ceil(
          remainingCooldown / 1000
        )} seconds before retrying`
      );
    }

    this.isCreatingGuestSession = true;
    this.lastGuestSessionAttempt = now;

    try {
      logger.debug('Creating guest session...');

      let attempt = 0;
      const maxAttempts = 3;
      let lastError: unknown;

      while (attempt < maxAttempts) {
        try {
          await this.account.createAnonymousSession();

          this.currentUser = await this.account.get();
          logger.debug('Guest session created:', this.currentUser.$id);

          const guestUser: AppUser = {
            ...this.currentUser,
            name: 'Guest User',
            isGuest: true,
          };

          this.currentUser = guestUser;
          await this.createUserDefaultSettings();

          return guestUser;
        } catch (error) {
          lastError = error;
          attempt++;

          const errorMessage = error instanceof Error ? error.message : '';
          const errorCode =
            error != null && typeof error === 'object' && 'code' in error
              ? (error as { code: unknown }).code
              : undefined;
          if (errorMessage.includes('Rate limit') || errorCode === 429) {
            const backoffDelay = Math.min(
              1000 * Math.pow(2, attempt),
              this.rateLimitCooldownMs
            );
            logger.warn(
              `Rate limited (attempt ${attempt}/${maxAttempts}), waiting ${
                backoffDelay / 1000
              }s before retry...`
            );

            if (attempt < maxAttempts) {
              await new Promise((resolve) => setTimeout(resolve, backoffDelay));
              continue;
            } else {
              this.lastGuestSessionAttempt = now + this.rateLimitCooldownMs;
              throw new Error(
                `Rate limit exceeded. Please wait ${
                  this.rateLimitCooldownMs / 1000
                } seconds before trying again.`
              );
            }
          } else {
            throw error;
          }
        }
      }

      throw lastError;
    } catch (error) {
      logger.error('Failed to create guest session:', error);
      throw error;
    } finally {
      this.isCreatingGuestSession = false;
    }
  }

  /**
   * Converts a guest account to a regular user by updating email/password
   * and clearing the anonymous session.
   */
  async upgradeGuestToUser(
    email: string,
    password: string,
    name: string
  ): Promise<AppUser> {
    if (!this.currentUser?.isGuest) {
      throw new Error('Can only upgrade guest accounts');
    }

    const guestUserId = this.currentUser.$id;
    if (!guestUserId) {
      throw new Error('Guest user ID not found');
    }

    try {
      logger.debug('Upgrading guest account:', guestUserId);
      await this.account.updateEmail({
        email: email,
        password: password,
      });
      await this.account.updateName({ name: name });

      logger.debug('Guest account upgrade completed:', guestUserId);

      // Clear guest session before creating authenticated session
      try {
        await this.account.deleteSession({ sessionId: 'current' });
        logger.debug('Guest session cleared');
      } catch (sessionError) {
        logger.warn(
          'Could not clear guest session (may already be invalid):',
          sessionError
        );
      }

      await this.account.createEmailPasswordSession({ email, password });
      const authenticatedUser = await this.account.get<UserSettings>();

      try {
        this.currentUser.prefs = await this.account.getPrefs<UserSettings>();
        logger.debug('User profile loaded');
      } catch (error) {
        logger.warn('Failed to load user profile after upgrade:', error);
      }

      this.currentUser = { ...authenticatedUser, isGuest: false };
      logger.debug(
        'Guest upgraded to authenticated user:',
        this.currentUser.name
      );

      return this.currentUser;
    } catch (error) {
      logger.error('Failed to upgrade guest account:', error);
      throw error;
    }
  }

  isGuest(): boolean {
    return !!this.currentUser && !this.currentUser.email;
  }

  isInRateLimitCooldown(): boolean {
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastGuestSessionAttempt;
    return timeSinceLastAttempt < this.guestSessionCooldownMs;
  }

  getRemainingCooldown(): number {
    if (!this.isInRateLimitCooldown()) return 0;
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastGuestSessionAttempt;
    const remainingMs = this.guestSessionCooldownMs - timeSinceLastAttempt;
    return Math.ceil(remainingMs / 1000);
  }

  /** @internal Checks if the guest user has any data (bank accounts, transactions) worth migrating. */
  async hasGuestData(): Promise<boolean> {
    if (!this.currentUser?.isGuest) {
      return false;
    }

    try {
      const bankAccounts = await this.tables.typed.listRows(
        APP_COLLECTIONS.BANK_ACCOUNTS
      );

      if (bankAccounts.total > 0) {
        logger.debug(
          `Guest has ${bankAccounts.total} bank accounts - migration needed`
        );
        return true;
      }

      const transactions = await this.tables.typed.listRows(
        APP_COLLECTIONS.TRANSACTIONS
      );

      if (transactions.total > 0) {
        logger.debug(
          `Guest has ${transactions.total} transactions - migration needed`
        );
        return true;
      }

      logger.debug('Guest has no valuable data, simple sign-in will be used');
      return false;
    } catch (error) {
      logger.warn(
        'Could not check guest data, assuming migration needed:',
        error
      );
      return true;
    }
  }

  /** @internal Simple sign-in without data migration (for guests with no data). */
  async simpleSignInFromGuest(
    email: string,
    password: string
  ): Promise<AppUser> {
    if (!this.currentUser?.isGuest) {
      throw new Error('Can only be called from guest session');
    }

    try {
      logger.debug('Performing simple sign-in (no data to migrate)');

      try {
        await this.account.deleteSession('current');
        logger.debug('Guest session cleared');
      } catch (sessionError) {
        logger.warn(
          'Could not clear guest session (may already be invalid):',
          sessionError
        );
      }

      await this.account.createEmailPasswordSession({ email, password });
      const authenticatedUser = await this.account.get<UserSettings>();

      try {
        this.currentUser.prefs = await this.account.getPrefs<UserSettings>();
        logger.debug('User profile loaded');
      } catch (error) {
        logger.warn('Failed to load user profile after sign-in:', error);
      }

      this.currentUser = { ...authenticatedUser, isGuest: false };
      logger.debug('Simple sign-in completed:', this.currentUser.name);

      return this.currentUser;
    } catch (error) {
      logger.error('Simple sign-in failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const authService = container.resolve(AppwriteAuthService);
